import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Navigation, Pagination } from 'swiper/modules';
import { API_BASE } from '../../config/api';
import { bindStorefrontRefresh } from '../../utils/storefrontRefresh';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import './SpecialDaysSection.css';

const DEFAULT_SECTION = {
  isEnabled: false,
  heading: 'Special Day',
  subtitle: '',
  ctaText: 'Shop Now',
  redirectLink: '/store?recipient=gents',
  backgroundColor: '#fff7f2',
  items: [],
};

const normalizeItems = (items = []) =>
  (Array.isArray(items) ? items : [])
    .map((item, index) => ({
      _id: item._id,
      key: item.key || `special-day-item-${index + 1}`,
      imageUrl: String(item.imageUrl || '').trim(),
      title: String(item.title || '').trim(),
      redirectLink: String(item.redirectLink || '').trim(),
      order: Number(item.order || index + 1),
      isVisible: item.isVisible !== false,
    }))
    .filter((item) => item.isVisible && item.imageUrl)
    .sort((leftItem, rightItem) => leftItem.order - rightItem.order);

const resolveNavigateTarget = (link = '') => {
  const nextLink = String(link || '').trim();

  if (!nextLink) {
    return DEFAULT_SECTION.redirectLink;
  }

  return nextLink.startsWith('/') || /^https?:\/\//i.test(nextLink)
    ? nextLink
    : `/${nextLink}`;
};

function SpecialDaysSection() {
  const navigate = useNavigate();
  const [section, setSection] = useState(DEFAULT_SECTION);

  const fetchSection = useCallback(() => {
    let active = true;

    fetch(`${API_BASE}/special-days-section`, {
      headers: { 'Content-Type': 'application/json' },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Failed to fetch Special Days section');
        }

        return response.json();
      })
      .then((data) => {
        if (!active) {
          return;
        }

        setSection({
          isEnabled: data?.isEnabled === true,
          heading: data?.heading || DEFAULT_SECTION.heading,
          subtitle: data?.subtitle || '',
          ctaText: data?.ctaText || DEFAULT_SECTION.ctaText,
          redirectLink: data?.redirectLink || DEFAULT_SECTION.redirectLink,
          backgroundColor: data?.backgroundColor || DEFAULT_SECTION.backgroundColor,
          items: normalizeItems(data?.items),
        });
      })
      .catch((error) => {
        console.error('Failed to load Special Days section:', error);
        if (active) {
          setSection(DEFAULT_SECTION);
        }
      });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const cleanup = fetchSection();
    return cleanup;
  }, [fetchSection]);

  useEffect(() => bindStorefrontRefresh(() => fetchSection(), { scopes: ['all', 'homepage'] }), [fetchSection]);

  const visibleItems = useMemo(() => normalizeItems(section.items), [section.items]);
  const showSliderControls = visibleItems.length > 1;

  if (!section.isEnabled || visibleItems.length === 0) {
    return null;
  }

  const handleNavigate = (link = '') => {
    const target = resolveNavigateTarget(link || section.redirectLink);

    if (/^https?:\/\//i.test(target)) {
      window.location.href = target;
      return;
    }

    navigate(target);
  };

  return (
    <section
      className="special-days-section"
      style={{ '--special-days-bg': section.backgroundColor || DEFAULT_SECTION.backgroundColor }}
    >
      <div className="special-days-inner">
        <div className="special-days-copy">
          <span className="special-days-kicker">Limited Occasion</span>
          <h2>{section.heading}</h2>
          {section.subtitle ? <p>{section.subtitle}</p> : null}
          <button type="button" className="special-days-cta" onClick={() => handleNavigate(section.redirectLink)}>
            {section.ctaText}
          </button>
        </div>

        <div className="special-days-slider-wrap" aria-label={section.heading}>
          {showSliderControls ? (
            <>
              <button
                type="button"
                className="special-days-nav special-days-nav--prev"
                aria-label="View previous Special Day images"
                onClick={(event) => event.stopPropagation()}
              >
                <FiChevronLeft />
              </button>
              <button
                type="button"
                className="special-days-nav special-days-nav--next"
                aria-label="View next Special Day images"
                onClick={(event) => event.stopPropagation()}
              >
                <FiChevronRight />
              </button>
            </>
          ) : null}

          <Swiper
            className="special-days-slider"
            modules={[Autoplay, Navigation, Pagination]}
            slidesPerView={1}
            spaceBetween={12}
            loop={visibleItems.length > 3}
            navigation={
              showSliderControls
                ? {
                    prevEl: '.special-days-nav--prev',
                    nextEl: '.special-days-nav--next',
                  }
                : false
            }
            pagination={
              showSliderControls
                ? {
                    el: '.special-days-pagination',
                    clickable: true,
                  }
                : false
            }
            autoplay={
              showSliderControls
                ? {
                    delay: 3800,
                    disableOnInteraction: false,
                    pauseOnMouseEnter: true,
                  }
                : false
            }
            breakpoints={{
              0: { slidesPerView: 1.35, spaceBetween: 10 },
              560: { slidesPerView: 2, spaceBetween: 12 },
              900: { slidesPerView: 3, spaceBetween: 16 },
              1180: { slidesPerView: 4, spaceBetween: 18 },
            }}
          >
            {visibleItems.map((item, index) => (
              <SwiperSlide key={item._id || item.key || `${item.imageUrl}-${index}`}>
                <button
                  type="button"
                  className="special-days-card"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleNavigate(item.redirectLink || section.redirectLink);
                  }}
                >
                  <img src={item.imageUrl} alt={item.title || section.heading} loading="lazy" />
                  {item.title ? <span>{item.title}</span> : null}
                </button>
              </SwiperSlide>
            ))}
          </Swiper>

          {showSliderControls ? <div className="special-days-pagination" aria-hidden="true" /> : null}
        </div>
      </div>
    </section>
  );
}

export default SpecialDaysSection;
